import { Box, Button, Typography } from '@mui/material';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { useInstallationGuide } from '../../hooks/useInstallationGuide';
import type { StorylineContext } from '../../services/storylineContext';

/** Path to the AI sparkle icon */
const AI_ICON_PATH = '/assets/images/installation-guide/ai-symbol.png';

interface GuidanceButton {
  /** Button label text */
  label: string;
  /** Chatbot flow ID to trigger when clicked */
  flowId: string;
}

interface GuidanceSectionProps {
  /** Array of help buttons to display */
  buttons: GuidanceButton[];
}

const CANONICAL_MAIN_STEP_IDS = new Set([
  'step_1_preInstall',
  'step_2_confirmCore',
  'step_3_boxContents',
  'step_4_cableToRouter',
]);

const CANONICAL_HELP_FLOW_IDS = new Set([
  'credentials-help',
  'wifi-help',
  'core-select',
  'missing-broken',
  'identify-router',
  'cable-too-short',
  'no-open-ports',
  'power-cable-too-short',
]);

function getCanonicalPromptMainStepId(mainStepId: string | null | undefined): string | undefined {
  if (!mainStepId) {
    return undefined;
  }

  if (mainStepId === 'step_boxContents') {
    return 'step_3_boxContents';
  }

  if (mainStepId === 'step_cableToRouter') {
    return 'step_4_cableToRouter';
  }

  return CANONICAL_MAIN_STEP_IDS.has(mainStepId) ? mainStepId : undefined;
}

function buildGuideContext(
  mainStepId: string | null | undefined,
  flowId: string
): StorylineContext | undefined {
  const canonicalMainStepId = getCanonicalPromptMainStepId(mainStepId);

  if (!canonicalMainStepId) {
    return undefined;
  }

  const canonicalHelpFlowId = CANONICAL_HELP_FLOW_IDS.has(flowId)
    ? flowId
    : undefined;

  return {
    mainStepId: canonicalMainStepId,
    ...(canonicalHelpFlowId ? { helpFlowId: canonicalHelpFlowId } : {}),
  };
}

/**
 * GuidanceSection - "Get additional guidance" component for installation steps
 * 
 * Displays an AI sparkle icon, label text, and pill-shaped buttons that
 * open the chatbot with specific flow IDs when clicked.
 * 
 * Uses the modal route pattern to navigate to /chatbot with state containing
 * the current location as background (for analytics parity with Storyline).
 */
export const GuidanceSection: React.FC<GuidanceSectionProps> = ({ buttons }) => {
  const history = useHistory();
  const location = useLocation();
  const { mainStepId } = useInstallationGuide();

  const handleButtonClick = (flowId: string) => {
    const guideContext = buildGuideContext(mainStepId, flowId);

    // Navigate to /chatbot with modal route state
    history.push('/chatbot', {
      background: location,
      flowId,
      ...(guideContext ? { guideContext } : {}),
    });
  };

  if (buttons.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 3 }}>
      {/* Header with AI icon and label */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1.5,
        }}
      >
        <Box
          component="img"
          src={AI_ICON_PATH}
          alt=""
          sx={{
            width: 20,
            height: 20,
          }}
        />
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            color: 'text.secondary',
          }}
        >
          {__('Get additional guidance.')}
        </Typography>
      </Box>

      {/* Pill buttons */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        {buttons.map((button) => (
          <Button
            key={`${button.flowId}-${button.label}`}
            variant="outlined"
            size="small"
            onClick={() => handleButtonClick(button.flowId)}
            sx={{
              borderRadius: 4, // Pill shape
              textTransform: 'none',
              px: 2,
              py: 0.75,
              fontSize: '0.875rem',
              borderColor: 'divider',
              color: 'text.primary',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
              },
            }}
          >
            {button.label}
          </Button>
        ))}
      </Box>
    </Box>
  );
};
